import { instanceToPlain } from 'class-transformer';
import { Component } from 'react';
import { cloneDeep } from 'xconvert-web-shared';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { UIContext } from '../../services/ui-context';
import { WebSocketService } from '../../services/websocket.service';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { gifEstimateStore } from '../../utils/gif-estimate.store';
import { PageData } from '../../types/route-types';
import { OrientationUpdateEvent, ProcessTypes } from '../../types/types';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { scheduleCoordinator } from '../../services/scheduling/schedule-coordinator';
import { isPerFileMergeType } from '../../utils/merge-type.utils';
import { isUploadExpiredError } from '../../utils/upload-expiry';
import { CONVERTER_RENDERED_REFUSALS } from '../../services/scheduling/schedule-refusals';
import { ConvertOptionsContextV2 } from '../convert-options-v2/convert-options.context';
import FileCardComponent, { UpdateProcessProbedDataFnProvider } from '../ui/file-card/file-card.component';
import SortableComponent from '../ui/sortable/sortable.component';
// Two leaves, and both are deliberate under D10. `studio-gate.ts` imports NOTHING at all, and
// `views/hold-defaults.ts`'s only import is `import type { StudioDocument }`, which TypeScript
// erases outright -- so at run time neither adds an edge to this file's graph, let alone the
// container edge that crashes a webpack module eval (`file-convert.component.tsx:813-822`).
// Verified at source 2026-09-17; if `hold-defaults.ts` ever takes a value import, the constants
// move to a leaf of their own rather than this import being widened.
import { SOLO_HOLD_MS } from '../studio/views/hold-defaults';
import { pageAcceptsStudio } from '../studio/studio-gate';
// Type-only: `host.ts` is types-only itself (no runtime code, no React import), so this adds no
// edge to the graph at run time. It is the ONE definition of what a successful conversion hands
// back (FRAME_PICKER_DESIGN.md §6.1 host interface).
import type { ConvertedEvent } from '../frame-picker/host';
import { convertEligible } from '../../utils/convert-eligible';
import { cardUploadIds, fileFingerprint, PendingRun } from '../../utils/rerun-canonical';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import {
    refusesTarget, unsupportedKindOf, unsupportedRefusalOf, unsupportedStagedStatus, unsupportedVerdictOf, UnsupportedInputCode,
} from '../../utils/unsupported-input';

interface FileProcessEditorProps {
    initialFiles?: UploadInProgressFile<any>[];
    convertOptionsContext: ConvertOptionsContextV2;
    fromType?: string[];
    toType: string;
    processType: string;
    pageData: PageData;
    onFilesChange?: (files: UploadInProgressFile<any>[]) => void;
    /**
     * The user pressed Convert. Raised before anything is scheduled, because the only caller so
     * far needs to read a piece of its OWN state that a conversion is about to clear — the Studio
     * nudge banner, whose NUDGE_DECLINED is a fact about what the user did instead (§8).
     */
    onConversionStart?: () => void;
    /**
     * Every card in the batch has been handed to the scheduler. The event names the inputs and
     * the workflow ids the schedule responses carried (`FileUpload.workflowId`; absent on the
     * `{queued:true}` path, where the coordinator owns the card and the id is not known yet).
     * The existing caller ignores the argument and navigates; the picker's host mirrors it onto
     * `onConverted$` so the picker can record `sid → workflow` BEFORE `/downloads` (§6.1, §6.4).
     */
    onConversionSuccess?: (event: ConvertedEvent) => void;
    probeDataProvider?: UpdateProcessProbedDataFnProvider;
    hideRemoveButton?: boolean;
    children?: React.ReactNode;
    /**
     * FIX-2 (UX-SPEC §2.1 T3): the server refused one or more cards of this batch with 410 UPLOAD_EXPIRED. Those cards
     * were discarded WITHOUT the remote delete (nothing was created; the original card and its output are untouched), no
     * dialog was shown, and `onConversionSuccess` is NOT raised for this batch -- the caller decides what happens next
     * (the Re-edit modal shows X2-3; the tool page re-uploads or marks the row). `scheduled` are the cards that went through.
     * Without this prop the editor raises `onConversionSuccess` for the scheduled cards only, and nothing when there are none.
     */
    onUploadExpired?: (event: UploadExpiredEvent) => void;
}

/**
 * FIX-2: what `onUploadExpired` is told. `files` are the staged rows each refused card was made from; `scheduledFiles`
 * are the rows of the cards that went through, so a page that stays (to show X2-4) can stop offering them to Convert
 * again (a second press would schedule a duplicate job).
 */
export interface UploadExpiredEvent {
    expired: { fileUpload: FileUpload; files: UploadInProgressFile<any>[] }[];
    scheduled: FileUpload[];
    scheduledFiles: UploadInProgressFile<any>[];
}

interface FileProcessEditorState {
    files: UploadInProgressFile<any>[];
}

export class FileProcessEditorComponent extends Component<FileProcessEditorProps, FileProcessEditorState> {
    private readonly logger = Logger.getLogger('FileProcessEditorComponent');

    @lazyInject(FileProcessService) fileProcessService: FileProcessService;
    @lazyInject(WebSocketService) websocketService: WebSocketService;
    @lazyInject(UIContext) uiContext: UIContext;
    @lazyInject(FileManagerService) fileManagerService: FileManagerService;
    @lazyInject(MetricsService) private metricsService: MetricsService;

    constructor(props: FileProcessEditorProps) {
        super(props);
        this.state = {
            files: props.initialFiles || []
        };
    }

    componentDidMount() {
        this.watchUnsupportedVerdicts(this.state.files);
    }

    componentDidUpdate(prevProps: FileProcessEditorProps, prevState: FileProcessEditorState) {
        if (this.state.files !== prevState.files) {
            this.watchUnsupportedVerdicts(this.state.files);
            // The editor owns the file list, so it owns estimate-record lifecycle:
            // any entry that left the list (remove button, wholesale updateFiles)
            // drops its record and marks in-flight probes stale. Sorting keeps the
            // same entries, so nothing is dropped there.
            const currentIds = new Set(this.state.files.filter(f => f).map(f => f.id));
            prevState.files.filter(f => f && !currentIds.has(f.id))
                .forEach(f => gifEstimateStore.remove(f.id));
            if (this.props.onFilesChange) {
                this.props.onFilesChange(this.state.files);
            }
        }
    }

    componentWillUnmount() {
        // Page navigation / editor teardown: cancel every tracked estimate
        // (binding lifecycle guard — no leaked promises or timers).
        this.state.files.filter(f => f).forEach(f => gifEstimateStore.remove(f.id));
    }

    public addFiles(newFiles: UploadInProgressFile<any>[]) {
        this.setState(prevState => ({
            files: [...prevState.files, ...newFiles]
        }));
        // FIX-6: watched here as well as in componentDidUpdate (see updateFiles).
        this.watchUnsupportedVerdicts(newFiles);
    }

    public updateFiles(files: UploadInProgressFile<any>[]) {
        this.setState({ files });
        // FIX-6 (UX2-01 / QA2-02): the upload manager's `fileListChanged` hands over the SAME array it mutates in
        // place (file-convert.component.tsx, `fileSelected`), and the page's `initialFiles` is that array too, so
        // `componentDidUpdate` sees `files === prevState.files` and never learnt about a new row: its verdict was only
        // applied at the Convert press. The rows are watched here directly (each row once).
        this.watchUnsupportedVerdicts(files);
    }

    public getFiles() {
        return this.state.files;
    }

    removeFile(file: any) {
        this.setState(prevState => {
            const newFiles = prevState.files.filter(f => f.getFile() !== file);
            return { files: newFiles };
        });
    }

    orientationChanged(orientationData: OrientationUpdateEvent) {
        const uploadInProgressFile = this.state.files.find(fm => fm && fm.getFile() === orientationData.file);
        if (!uploadInProgressFile) {
            this.logger.trace1('Set orientation was called on a file that is not on uploadInProgressFiles list');
            return;
        }
        uploadInProgressFile.setOrientation(orientationData.orientation);
        this.forceUpdate();
    }

    /** Rows whose upload result is already being watched for a FIX-6 verdict (each row once). */
    private readonly unsupportedWatched = new WeakSet<UploadInProgressFile<any>>();

    /**
     * FIX-6 (UX2-01): how many rows a verdict has blocked so far, and the count at which each row was blocked. The
     * page reads the count when the user presses Convert (`unsupportedVerdictMark`) and hands it back to
     * `startConversion`, which can then tell a refusal the user already saw from one that landed after the press.
     */
    private unsupportedVerdictCount = 0;
    private readonly unsupportedVerdictAt = new WeakMap<UploadInProgressFile<any>, number>();

    public get unsupportedVerdictMark(): number {
        return this.unsupportedVerdictCount;
    }

    /**
     * FIX-6 (UX-SPEC section 6, "Preferred"): when a row's upload result says the edge cannot convert the file
     * for this page's target, the row says so -- "Can't convert" + the reason + Remove -- and `convertEligible`
     * leaves it out of Convert, so the rest of the batch still converts. The upload result carries the verdict
     * only while the server's `unsupportedInputDetection` is on; without one nothing changes.
     */
    private watchUnsupportedVerdicts(files: UploadInProgressFile<any>[]) {
        (files || []).forEach(f => {
            if (!f || this.unsupportedWatched.has(f) || typeof f.whenResponseReady !== 'function') return;
            this.unsupportedWatched.add(f);
            f.whenResponseReady().then(() => this.applyUnsupportedVerdict(f)).catch(() => { /* no verdict */ });
        });
    }

    /** Marks one row blocked if its verdict refuses this page's target; true when it did. */
    private applyUnsupportedVerdict(f: UploadInProgressFile<any>): boolean {
        if (!f || f.stagedStatus?.kind === 'blocked') return false;
        if (!this.state.files.includes(f)) return false;
        const verdict = unsupportedVerdictOf(f.getResponse?.());
        if (!verdict || !refusesTarget(verdict, this.props.toType)) return false;
        this.blockUnsupported(f, verdict.code, 'staged_row');
        return true;
    }

    private blockUnsupported(f: UploadInProgressFile<any>, code: UnsupportedInputCode, surface: 'staged_row') {
        f.setStagedStatus(unsupportedStagedStatus(code, () => this.removeFile(f.getFile())));
        this.unsupportedVerdictAt.set(f, ++this.unsupportedVerdictCount);
        try {
            this.metricsService?.emit(MetricEventName.UPLOAD_UNSUPPORTED_DETECTED, {
                kind: unsupportedKindOf(code),
                surface,
                page: this.props.pageData?.path || '',
            });
        } catch { /* observability never breaks the page */ }
        // A new list, so the page (Convert button, side panel) re-reads which rows Convert may send.
        this.setState(prev => ({ files: [...prev.files] }));
    }

    sortOrderChanged(e: any) {
        // Optional: Notify parent if needed, but state update happens in sortEnded usually or here if real-time
    }

    sortEnded(e: any) {
        this.setState({ files: e });
    }

    /**
     * "A single static photo shows for 8 seconds" — STUDIO_DESIGN.md §9 item 2.
     *
     * xconfig cannot express a default that depends on the number of inputs (the Phase 0 ruling
     * says so and moved the rule here: `STUDIO_PLAN_P0/progress.md:9,58`), and an Options V2
     * summary carries no "the user touched this" bit. So the test is "the summary still says what
     * the freshly built context said": if it does, nobody chose the value and 8 is the better
     * default; if it does not, someone chose and we leave it alone.
     *
     * THE COST, RECORDED. A user who deliberately types `8` on a one-photo page is
     * indistinguishable from one who typed nothing — and gets 8 either way, which is the same
     * outcome. A user who types `3` keeps 3. That is the case this rule must not break, and the
     * cheapest rule that cannot break it.
     *
     * Scoped to the image→video family by the SAME predicate the Studio tab uses, so a one-file
     * PDF merge or a one-file compress is untouched. `SOLO_HOLD_MS` is milliseconds and
     * `imageDuration` is whole seconds — the one conversion, in one place.
     */
    private applySoloPhotoHold(summary: any, files: UploadInProgressFile<any>[] = this.state.files): any {
        if (!summary || !pageAcceptsStudio(this.props.pageData)) return summary;
        if (files.length !== 1) return summary;
        const defaults = this.props.convertOptionsContext.getDefaultSummary?.();
        const current = summary.imageDuration;
        if (current === undefined || defaults === undefined) return summary;
        if (String(current) !== String(defaults.imageDuration)) return summary;
        return { ...summary, imageDuration: String(SOLO_HOLD_MS / 1000) };
    }

    prepareMergeOptionsAndSnapshot(files: UploadInProgressFile<any>[] = this.state.files) {
        const summary = instanceToPlain(this.applySoloPhotoHold(this.props.convertOptionsContext.getSummary(), files));
        const summaryJson = JSON.parse(JSON.stringify(summary));
        const optionsSnapshot = cloneDeep(summaryJson);

        const orientationAngleList: string[] =
            files.map(fm => (fm.getOrientation() && fm.getOrientation().angle) || '0');
        const orientationFlipList: string[] =
            files.map(fm => (fm.getOrientation() && fm.getOrientation().isFliped) || 'false');
        summaryJson.orientationAngleList = orientationAngleList;
        summaryJson.orientationFlipList = orientationFlipList;
        return { options: summaryJson, optionsSnapshot };
    }

    /**
     * Determines if files should be merged into a single output.
     * Checks both processType and mergeType options.
     */
    private shouldMergeFiles(): boolean {
        const summary = this.props.convertOptionsContext.getSummary() as any;
        const mergeType = summary?.mergeType;
        if (isPerFileMergeType(mergeType)) return false;
        if (this.props.processType === ProcessTypes.MERGE || this.props.processType === ProcessTypes.ARCHIVE) {
            return true;
        }
        return mergeType === 'MERGE_PDF' || mergeType === 'MERGE_ARCHIVE' || mergeType === 'MERGE_VIDEO' || mergeType === 'MERGE_ANIMATED';
    }

    /**
     * Set while one batch's cards are being dispatched, so the queued/stalled
     * outcome produces AT MOST ONE toast for the whole batch rather than one
     * modal per file (plan §4.4: "Batch header row, one toast per batch, at most
     * one modal"). During the 2026-09-05 incident a 340-file batch would have
     * opened 340 "Connection Issue" dialogs on top of each other.
     */
    private batchQueuedNoticeShown = false;

    /**
     * FIX-8 (UX-SPEC.md section 8.1): what `convertFiles(files)` WOULD post, one entry per card it
     * would create -- built by the very same calls (`shouldMergeFiles`, `prepareMergeOptionsAndSnapshot`,
     * the solo-photo hold, `createSingleFileUploads` with its per-file orientation lists), so the
     * "unchanged" check compares the request itself and can never drift from it. Read only: nothing
     * is added to the file manager and nothing is sent.
     */
    public describeScheduleRequests(files: UploadInProgressFile<any>[] = this.state.files): PendingRun[] {
        // What startConversion would really send: rows a fix marked `blocked` are not posted (PLAN.md C6).
        const list = convertEligible(files || []).filter(f => f);
        if (list.length === 0) return [];
        const wsId = '';
        const uploads: FileUpload[] = this.shouldMergeFiles()
            ? (() => {
                const { options, optionsSnapshot } = this.prepareMergeOptionsAndSnapshot(list);
                return [FileUploadUtils.createMergeFileUpload(list, wsId, this.props.processType as any,
                    this.props.fromType as any, this.props.toType as any, options, optionsSnapshot, this.props.pageData)];
            })()
            : FileUploadUtils.createSingleFileUploads(list, wsId, this.props.processType as any, this.props.fromType as any,
                this.props.toType as any, this.applySoloPhotoHold(this.props.convertOptionsContext.getSummary(), list), this.props.pageData);
        const merged = uploads.length === 1 && list.length > 1;
        return uploads.map((upload, i) => {
            const inputs = merged ? list : [list[i]];
            return {
                uniqueProcessType: upload.uniqueProcessType,
                toType: upload.toType,
                uploadIds: cardUploadIds(upload),
                fingerprints: inputs.map(f => fileFingerprint(f?.file as any)),
                options: upload.options,
            };
        });
    }

    /**
     * Quick convert's button: every staged file Convert may send (`convertEligible`: all of them, unless a
     * fix marked a row `blocked` -- prod fixes 2026-10-02, PLAN.md C6). Nothing to send, nothing to start.
     *
     * `verdictMark` is `unsupportedVerdictMark` read when the user pressed Convert (the page reads it; a press while
     * uploads are still running starts later, from a timer). FIX-6 (UX2-01): when a row was refused AFTER that -- or by
     * this call, for a verdict that landed in the same tick -- nothing is sent: the user has not seen that row's "Can't
     * convert" yet, and going on would drop it silently on the way to /downloads. The row now says why, and the next
     * press converts the rest. Resolves false when nothing was sent.
     */
    public async startConversion(opts: { verdictMark?: number } = {}): Promise<boolean> {
        const mark = typeof opts.verdictMark === 'number' ? opts.verdictMark : this.unsupportedVerdictCount;
        // A verdict that landed in the same tick as the press is applied before the list is filtered.
        this.state.files.forEach(f => { if (f) this.applyUnsupportedVerdict(f); });
        const eligible = convertEligible(this.state.files);
        if (eligible !== this.state.files && eligible.filter(f => f).length === 0) return false;
        if (this.state.files.some(f => f && (this.unsupportedVerdictAt.get(f) ?? 0) > mark)) return false;
        try {
            await this.convertFiles(eligible);
        } catch (e) {
            // FIX-6: the server refused every file; the rows already say why, so there is nothing more to report.
            if ((e as { unsupportedInputRefused?: boolean })?.unsupportedInputRefused === true) return false;
            throw e;
        }
        return true;
    }

    /**
     * Convert the given staged files — THE ONE request path for both tabs (FRAME_PICKER_DESIGN.md
     * §0 "Convert", §6.1 host interface, §11.0 R10).
     *
     * `startConversion()` calls it with the whole list; the picker calls it through
     * `FramePickerHost.convertFiles([selectedFile])` with one. Both therefore share
     * `processFileWithFeedback` and everything it decides: the enforcement-error branch, the
     * `{queued: true}` sentinel, the inverted error branch and the single per-batch toast. The
     * picker owns no request code and can never drift from Quick convert's.
     *
     * `files` is used instead of `this.state.files` throughout — including the solo-photo hold
     * and the orientation lists — so the picker's one-file call sees exactly what a one-file
     * Quick convert would. The list is not required to be a subset of the staged list; the
     * caller is trusted to pass entries it got from `stagedFiles$`.
     *
     * Resolves after every card has been handed to the scheduler, then raises
     * `onConversionSuccess` with the inputs and the workflow ids known at that moment.
     */
    public async convertFiles(files: UploadInProgressFile<any>[], opts: { quiet?: boolean } = {}) {
        this.props.onConversionStart?.();
        // Round 6 (R6-I5, UX#13): `quiet` = the caller shows its own progress (the Frame picker), so
        // the per-batch "Connecting to server" toast is not raised over it.
        const quiet = opts.quiet === true;
        const processPromises: Promise<void>[] = [];
        const fileUploads: FileUpload[] = [];
        // FIX-2: cards the server refused as expired, with the staged rows they were made from.
        const expired: UploadExpiredEvent['expired'] = [];
        this.batchQueuedNoticeShown = false;
        // FIX-6 backstop: the cards the server refused with 422 UNSUPPORTED_INPUT (nothing was created for them).
        const refused = new Set<FileUpload>();

        if (this.shouldMergeFiles()) {
            const { options, optionsSnapshot } = this.prepareMergeOptionsAndSnapshot(files);
            const fileUpload = FileUploadUtils.createMergeFileUpload(
                files,
                this.websocketService.getId(),
                this.props.processType as any,
                this.props.fromType as any,
                this.props.toType as any,
                options,
                optionsSnapshot,
                this.props.pageData
            );

            this.fileManagerService.addFileUpload(fileUpload);
            fileUploads.push(fileUpload);
            processPromises.push(this.processFileWithFeedback(fileUpload, quiet, () => expired.push({ fileUpload, files: [...files] }), { inputs: files, refused }));
        } else {
            const created = FileUploadUtils.createSingleFileUploads(
                files,
                this.websocketService.getId(),
                this.props.processType as any,
                this.props.fromType as any,
                this.props.toType as any,
                this.applySoloPhotoHold(this.props.convertOptionsContext.getSummary(), files),
                this.props.pageData
            );

            created.forEach((fileUpload, i) => {
                this.fileManagerService.addFileUpload(fileUpload);
                fileUploads.push(fileUpload);
                // `createSingleFileUploads` maps the staged rows one to one, in order.
                processPromises.push(this.processFileWithFeedback(fileUpload, quiet, () => expired.push({ fileUpload, files: files[i] ? [files[i]] : [] }), { inputs: [files[i]], refused }));
            });
        }

        await Promise.all(processPromises);

        // FIX-6 (T8): a card the server refused with 422 UNSUPPORTED_INPUT is not a conversion (its rows say why).
        // FIX-2 (T3): a refused-as-expired card is not one either; the caller owns what happens next.
        let uploads = refused.size ? fileUploads.filter(fu => !refused.has(fu)) : fileUploads;
        if (expired.length > 0) {
            uploads = uploads.filter(fu => !expired.some(e => e.fileUpload === fu));
            if (this.props.onUploadExpired) {
                const refusedRows = new Set<UploadInProgressFile<any>>();
                expired.forEach(e => e.files.forEach(f => refusedRows.add(f)));
                this.props.onUploadExpired({
                    expired,
                    scheduled: uploads,
                    scheduledFiles: files.filter(f => !refusedRows.has(f) && f?.stagedStatus?.kind !== 'blocked'),
                });
                return;
            }
            if (uploads.length === 0) return;
        }
        // FIX-6: nothing scheduled -> no success (the page stays where the refused rows now say why) and a typed
        // rejection, so a caller that waits for an outcome -- the picker's host settles its batch on success or
        // failure -- is not left waiting; `startConversion` swallows it.
        if (refused.size && uploads.length === 0) {
            throw Object.assign(new Error('Every input was refused as unsupported (422 UNSUPPORTED_INPUT).'), { unsupportedInputRefused: true });
        }

        if (this.props.onConversionSuccess) {
            this.props.onConversionSuccess({
                workflowIds: uploads.map(fu => fu?.workflowId).filter((id): id is string => !!id),
                files: refused.size ? files.filter(f => f?.stagedStatus?.kind !== 'blocked') : files,
                // Round 14 (picker K4/N3): a card scheduled hands-off has no id yet; the picker watches these for it.
                uploads,
            });
        }
    }


    private processFileWithFeedback(
        fileUpload: FileUpload,
        quiet: boolean = false,
        onUploadExpired?: () => void,
        backstop?: { inputs: UploadInProgressFile<any>[]; refused: Set<FileUpload> },
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!quiet) this.uiContext.successToast('Starting conversion...', 'Connecting to server', 3000);
            // INT1-P2-02: this surface renders 410 UPLOAD_EXPIRED (below), so it asks professorx for it.
            this.fileProcessService.processFile(fileUpload, { rendersRefusals: CONVERTER_RENDERED_REFUSALS }).subscribe({
                next: (data) => {
                    // The hands-off path emits a `{queued:true}` sentinel as soon
                    // as the card has a state worth showing: the schedule call has
                    // NOT succeeded, so there is nothing to retrack — the card is
                    // already on the list and the coordinator owns it from here.
                    if ((data as any)?.queued === true) {
                        this.noteBatchQueued((data as any)?.stalled === true);
                        resolve();
                        return;
                    }
                    this.fileManagerService.retrackFileUpload(fileUpload);
                    this.logger.trace7(data);
                    resolve();
                },
                error: (err) => {
                    // FIX-2 (UX-SPEC §2.1 T3, C2): 410 UPLOAD_EXPIRED -- the server created nothing. This NEW card is
                    // forgotten without `removeFile` (whose remote delete is for "the user removed this file"), no
                    // "Error" or "Connection Issue" dialog, and the card it was re-edited from is never touched.
                    if (isUploadExpiredError(err)) {
                        this.logger.info({ message: 'Schedule refused: upload expired', data: { id: fileUpload?.id } });
                        this.fileManagerService.discardFileUpload(fileUpload);
                        onUploadExpired?.();
                        resolve();
                        return;
                    }
                    // FIX-6 backstop: professorx refused the submission with 422 UNSUPPORTED_INPUT before anything
                    // was created or counted (the page never saw the upload's verdict). No dialog: the card is
                    // forgotten WITHOUT deleting the bytes (a merge's other inputs must survive), and the refused
                    // staged rows say "Can't convert" like a verdict seen at upload would have. An expected answer
                    // with its own UI, so it is logged at info like FIX-2's 410, never as "File process failed." (fu-t8).
                    const refusal = unsupportedRefusalOf(err);
                    if (refusal) {
                        this.logger.info({ message: 'Schedule refused: unsupported input', data: { id: fileUpload?.id, kinds: refusal.kinds } });
                        this.fileManagerService.discardFileUpload(fileUpload);
                        backstop?.refused.add(fileUpload);
                        refusal.inputs.forEach((n, j) => {
                            const row = backstop?.inputs?.[n - 1];
                            const code = refusal.kinds[j] ?? refusal.kinds[0];
                            if (row && code && row.stagedStatus?.kind !== 'blocked') this.blockUnsupported(row, code, 'staged_row');
                        });
                        resolve();
                        return;
                    }
                    this.logger.error({ message: 'File process failed.', error: err, data: FileUpload.toLoggableObject(fileUpload) });
                    if (err?.isEnforcementError) {
                        // Keep file on card — don't remove. Set enforcement error for display.
                        fileUpload.result = { error: true, dataType: 'enforcement_error', data: JSON.stringify(err.enforcementData) };
                        // Code 6: the card parses `ws.data` as the envelope (`decodeDataType`) and words it itself —
                        // the server's `userMessage` names the row and the raw counters, so it is never shown as-is.
                        fileUpload.ws = { processStage: 'COMPLETED', processStageSubState: 'FAILED', dataType: 'enforcement_error', data: JSON.stringify(err.enforcementData ?? {}) } as any;
                        this.fileManagerService.saveFileUploadToStorage(fileUpload);
                        resolve();
                        return;
                    }
                    // THE INVERTED ERROR BRANCH (plan §4.1, correction 16;
                    // clientQueuedState only). 2026-09-05: `removeFile` used to run
                    // HERE, before either dialog — and it fires
                    // `GET /file-manager/delete/<id>` on the upload, process AND
                    // download domains, so the uploaded bytes were destroyed before
                    // the "Connection Issue" dialog offered a Retry that could no
                    // longer work. Under the flag the ONLY failure that still
                    // deletes bytes is a deterministic 4xx (`scheduleTerminal`):
                    // a 5xx, a 409, a timeout and an offline browser all keep the
                    // card AND the file, and the card says so.
                    if (scheduleCoordinator.enabled && !err?.scheduleTerminal) {
                        this.noteBatchQueued(true);
                        resolve();
                        return;
                    }
                    this.fileManagerService.removeFile(fileUpload);
                    if (ConnectivityUtils.isConnectivityError(err) || ConnectivityUtils.isRetryableUploadError(err)
                        || err?.message?.includes('All requests failed')) {
                        this.uiContext.showErrorDialog({
                            title: 'Connection Issue',
                            text: 'Could not reach the processing server. This is usually caused by a network issue. Please check your connection and try again.',
                            confirmButtonText: 'Retry',
                            successCallback: () => {
                                this.fileManagerService.addFileUpload(fileUpload);
                                this.processFileWithFeedback(fileUpload).then(resolve).catch(reject);
                            },
                            showCancelButton: true,
                            cancelButtonText: 'Cancel',
                            cancelCallback: () => reject(err),
                        });
                    } else {
                        this.uiContext.showErrorDialog({
                            title: 'Error',
                            text: `Failed to ${this.props.processType} the file. ${err?.message || 'Please try again.'}`,
                        });
                        reject(err);
                    }
                }
            });
        });
    }

    /**
     * At most ONE toast for the whole batch (plan §4.4). No per-file modal: the
     * card itself says "Queued — server busy, retrying" / "Not started — your
     * file is still here", and the batch header on /downloads carries the single
     * "Retry all". Provenance: 2026-09-05 incident, reviewer finding SaaS #5.
     */
    private noteBatchQueued(stalled: boolean) {
        if (this.batchQueuedNoticeShown) return;
        this.batchQueuedNoticeShown = true;
        if (stalled) {
            this.uiContext.warnToast(
                'Not started yet',
                'The server is busy. Your files are safe — use "Retry all" on the downloads page when you are ready.',
                false, false,
            );
            return;
        }
        this.uiContext.successToast(
            'Queued',
            'The server is busy right now. We keep retrying — your files stay where they are.',
            6000,
        );
    }

    render() {
        return (
            <div className={`container-fluid g-0`}>
                <SortableComponent
                    items={this.state.files}
                    sortHandlerClass='sort-handler'
                    classes={['row g-0']}
                    orderChanged={(e) => this.sortOrderChanged(e)}
                    sortEnded={(e) => this.sortEnded(e)}>
                    {
                        this.state.files.filter(f => f).map((uploadInProgressFile, idx) => {
                            return <div key={`${uploadInProgressFile.uuid}-parent-${idx}`}
                                data-notsortable={false}
                                data-uniquekey={uploadInProgressFile.uuid}
                                className={`sortable ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image' : 'image'} col-12`}
                            >
                                <FileCardComponent
                                    uploadInProgressFile={uploadInProgressFile}
                                    convertOptionsContext={this.props.convertOptionsContext}
                                    convertOptionCategories={this.props.convertOptionsContext} // Passing context as categories as per original usage
                                    updateProcessProbedDataFnProvider={this.props.probeDataProvider}
                                    toType={this.props.toType as any}
                                    fromType={this.props.fromType as any}
                                    processType={this.props.processType}
                                    removeFile={$event => this.removeFile($event)}
                                    hideRemoveButton={this.props.hideRemoveButton}
                                    orientationChanged={$event => this.orientationChanged($event)}>
                                </FileCardComponent>
                            </div>
                        })
                    }
                </SortableComponent>
                {this.props.children}
            </div>
        );
    }
}
